import onetime from "@-/com.npmjs.onetime";
import type { TransformerWorklet } from "@-/com.npmjs.react-native-transformer-text-input";
import {
  getPinyinSyllables,
  toDiacriticPinyin,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";

import { stripToneMarks } from "@-/com.npmjs.kensio__pinyinjs";
import { DIACRITIC_TONE_DICT } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin.diacritic.entities";

/** Build on JS once. The worklet captures plain data, never the non-worklet converter. */
export const createPinyinTextTransformer = onetime((): TransformerWorklet => {
  const displayByNumber: Record<string, string> = {};
  const numberByDisplay: Record<string, string> = {};
  const tonelessByLetter: Record<string, string> = {};
  for (const marked of Object.keys(DIACRITIC_TONE_DICT)) {
    tonelessByLetter[marked] = stripToneMarks(marked);
  }
  const combiningTones = "\u{0304}\u{0301}\u030C\u{0300}";
  for (const mark of combiningTones) {
    tonelessByLetter[mark] = "";
  }
  const pinyinLetters = "abcdefghijklmnopqrstuvwxyzüê^" + Object.keys(tonelessByLetter).join("");
  const syllables = getPinyinSyllables();
  for (const syllable of syllables) {
    for (const ending of ["", "r"]) {
      for (let tone = 0; tone <= 5; tone += 1) {
        const key = `${syllable}${ending}${tone}`;
        const display = toDiacriticPinyin(key);
        if (display !== key) {
          displayByNumber[key] = display;
          displayByNumber[key.replaceAll("ê", "e^")] = display;
          if (tone >= 1 && tone <= 4 && display !== `${syllable}${ending}`) {
            numberByDisplay[display.normalize("NFC")] = key;
          }
        }
      }
    }
  }

  return (input) => {
    "worklet";
    const { previousSelection, previousSourceValue = "", previousValue, selection } = input;
    let { value } = input;
    let nextSelection = selection;
    const explicitSpans: { end: number; source: string; start: number }[] = [];
    const neutralSpans: { end: number; source: string; start: number }[] = [];
    let sourceOffset = 0;
    // Neutral tone has no visible mark. Carry it only for words that survived this edit.
    previousSourceValue.replaceAll(/[\p{Script=Latin}\p{M}:^]+[0-5]/gu, (part, offset) => {
      const key = part.toLowerCase().replaceAll("u:", "v").replaceAll("ü", "v");
      const display = displayByNumber[key];
      if (display !== undefined) {
        const start = offset + sourceOffset;
        if (key.endsWith("0") || key.endsWith("5")) {
          neutralSpans.push({
            end: start + display.length,
            source: part.endsWith("5") ? `${part.slice(0, -1)}0` : part,
            start,
          });
        }
        sourceOffset += display.length - part.length;
      }
      return part;
    });
    let clearedToneStart = -1;
    // Only an actual collapsed-caret backward deletion removes a tone first.
    // Selection replacement, forward-delete, and programmatic writes retain normal semantics.
    const caret = previousSelection.start;
    const removedLength = previousValue.length - value.length;
    const removed = previousValue.slice(caret - removedLength, caret);
    const isSingleDeletion =
      removedLength === 1 || (removedLength === 2 && combiningTones.includes(removed.charAt(1)));
    const isLetter = (letter: string) => {
      "worklet";
      return (
        letter.length > 0 &&
        (pinyinLetters.includes(letter.toLowerCase()) || combiningTones.includes(letter))
      );
    };
    if (
      isSingleDeletion &&
      isLetter(removed.charAt(0)) &&
      previousSelection.end === caret &&
      selection.start === selection.end &&
      caret > 0 &&
      selection.start === caret - removedLength &&
      previousValue.slice(0, caret - removedLength) + previousValue.slice(caret) === value
    ) {
      let wordStart = caret - removedLength;
      let wordEnd = caret;
      while (wordStart > 0 && isLetter(previousValue.charAt(wordStart - 1))) {
        wordStart -= 1;
      }
      while (wordEnd < previousValue.length && isLetter(previousValue.charAt(wordEnd))) {
        wordEnd += 1;
      }
      const word = previousValue.slice(wordStart, wordEnd);
      let plain = "";
      let nextCaret = wordStart;
      let originalOffset = wordStart;
      for (const letter of word) {
        const plainLetter = tonelessByLetter[letter] ?? letter;
        plain += plainLetter;
        if (originalOffset < caret) {
          nextCaret += plainLetter.length;
        }
        originalOffset += letter.length;
      }
      if (
        plain !== word ||
        neutralSpans.some((span) => span.start === wordStart && span.end === wordEnd)
      ) {
        clearedToneStart = wordStart;
        nextSelection = {
          end: nextCaret,
          start: nextCaret,
        };
        value = previousValue.slice(0, wordStart) + plain + previousValue.slice(wordEnd);
      }
    }

    let start = nextSelection.start;
    let end = nextSelection.end;
    let displayOffset = 0;
    const nextValue = value.replaceAll(
      /[\p{Script=Latin}\p{M}:^]+[0-5]/gu,
      (part: string, offset: number) => {
        let key = "";
        for (const letter of part.toLowerCase().replaceAll("u:", "v")) {
          key += (tonelessByLetter[letter] ?? letter).replace("ü", "v");
        }
        let replacement = displayByNumber[key];
        if (replacement === undefined) {
          return part;
        }
        if (part === part.toUpperCase()) {
          replacement = replacement.toUpperCase();
        } else if (part.charAt(0) === part.charAt(0).toUpperCase()) {
          replacement = replacement.charAt(0).toUpperCase() + replacement.slice(1);
        }
        let source = key.endsWith("5") ? `${key.slice(0, -1)}0` : key;
        if (part === part.toUpperCase()) {
          source = source.toUpperCase();
        } else if (part.charAt(0) === part.charAt(0).toUpperCase()) {
          source = source.charAt(0).toUpperCase() + source.slice(1);
        }
        explicitSpans.push({
          end: offset + displayOffset + replacement.length,
          source,
          start: offset + displayOffset,
        });
        displayOffset += replacement.length - part.length;
        const mapPosition = (position: number) => {
          "worklet";
          if (position <= offset) {
            return position;
          }
          if (position >= offset + part.length) {
            return position + replacement.length - part.length;
          }
          return offset + Math.min(position - offset, replacement.length);
        };
        start += mapPosition(nextSelection.start) - nextSelection.start;
        end += mapPosition(nextSelection.end) - nextSelection.end;
        return replacement;
      },
    );
    let prefix = 0;
    while (
      prefix < previousValue.length &&
      prefix < nextValue.length &&
      previousValue.charAt(prefix) === nextValue.charAt(prefix)
    ) {
      prefix += 1;
    }
    let suffix = 0;
    while (
      suffix < previousValue.length - prefix &&
      suffix < nextValue.length - prefix &&
      previousValue.charAt(previousValue.length - suffix - 1) ===
        nextValue.charAt(nextValue.length - suffix - 1)
    ) {
      suffix += 1;
    }
    const retainedNeutral = neutralSpans.flatMap((span) => {
      "worklet";
      if (
        span.start === clearedToneStart ||
        (previousSelection.start < span.end && previousSelection.end > span.start)
      ) {
        return [];
      }
      if (span.end <= prefix) {
        return [span];
      }
      if (span.start >= previousValue.length - suffix) {
        const delta = nextValue.length - previousValue.length;
        return [{ ...span, end: span.end + delta, start: span.start + delta }];
      }
      return [];
    });
    let explicitIndex = 0;
    let neutralIndex = 0;
    const sourceValue = nextValue.replaceAll(/[\p{Script=Latin}\p{M}:^]+/gu, (word, offset) => {
      const canonicalWord = (part: string) => {
        "worklet";
        let canonical = numberByDisplay[part.toLowerCase().normalize("NFC")];
        if (canonical === undefined) {
          return part;
        }
        if (part === part.toUpperCase()) {
          canonical = canonical.toUpperCase();
        } else if (part.charAt(0) === part.charAt(0).toUpperCase()) {
          canonical = canonical.charAt(0).toUpperCase() + canonical.slice(1);
        }
        return canonical;
      };
      // Both span lists are ordered. Walk them once, including for a large pasted buffer.
      while (explicitIndex < explicitSpans.length) {
        const span = explicitSpans[explicitIndex];
        if (span === undefined || span.end > offset) {
          break;
        }
        explicitIndex += 1;
      }
      while (neutralIndex < retainedNeutral.length) {
        const span = retainedNeutral[neutralIndex];
        if (span === undefined || span.end > offset) {
          break;
        }
        neutralIndex += 1;
      }
      const first = explicitSpans[explicitIndex];
      if (first === undefined || first.start >= offset + word.length) {
        const neutral = retainedNeutral[neutralIndex];
        return neutral?.start === offset && neutral.end === offset + word.length
          ? neutral.source
          : canonicalWord(word);
      }
      let source = "";
      let cursor = 0;
      while (explicitIndex < explicitSpans.length) {
        const span = explicitSpans[explicitIndex];
        if (span === undefined || span.start >= offset + word.length) {
          break;
        }
        source += canonicalWord(word.slice(cursor, span.start - offset)) + span.source;
        cursor = span.end - offset;
        explicitIndex += 1;
      }
      return source + canonicalWord(word.slice(cursor));
    });
    return { selection: { end, start }, sourceValue, value: nextValue };
  };
});
